import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colorsV2 } from 'style/colors-v2';
import { CrownIcon } from 'components/Icon/SVGIcons';
import { Typography } from 'components/DesignSystemV2/Typography';
import IconUserGroup from 'components/Icon/SVGIcons/IconUserGroup';
import HonorRollList from './HonorRollList';
import axios from 'axios';
import { toScreen } from 'utils/media-query/responsive.util';
import { useDeviceDetect3 } from 'utils/hooks/useDeviceQuery.hook';
import { ResponsiveContainer } from 'components/ResponsiveContainer';

const Wrapper = styled.div`
  background-color: ${colorsV2.neutral5};
  padding: 100px 0;

  ${toScreen(775)} {
    padding: 64px 0;
  }
`;

const Inner = styled.div`
  display: grid;
  gap: 48px;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${toScreen(775)} {
    flex-direction: column;
    gap: 24px;
    text-align: center;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* To cut off the text, easy to responsive */
  max-width: 475px;

  ${toScreen(775)} {
    align-items: center;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${toScreen(775)} {
    align-items: center;
  }
`;

const HeaderRightItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HonorRoll = ({
  title = 'Bảng vàng thành tích của học viên DOL',
  subtitle = '250+ học viên',
  description = 'có kết quả trên 8.0 IELTS'
}) => {
  const [honorList, setHonorList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const total = useRef(0);
  const { isDesktop, isMobile } = useDeviceDetect3();

  const fetchHonorList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.NODE_ENDPOINT}/hallOfFameList`,
        {
          params: {
            program: 'IELTS',
            _sort: 'overallScore,resultDate',
            _page: page,
            _limit: isDesktop ? 8 : 6,
            _order: 'desc,desc'
          }
        }
      );
      const list = response.data;
      setHonorList(prev => [...prev, ...list]);
      if (total.current === 0) {
        total.current = response.headers['x-total-count'];
      }
    } catch (error) {
      console.error({ error });
    } finally {
      setLoading(false);
    }

    // Don't need to add isDesktop to dependency array
    // `limit` is defined once when the component is mounted
    // regardless of the screen size change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    fetchHonorList();
  }, [fetchHonorList]);

  function onLoadMoreClick() {
    setPage(page => page + 1);
  }

  return (
    <Wrapper>
      <ResponsiveContainer>
        <Inner>
          <Header>
            <HeaderLeft>
              <CrownIcon />
              <Typography
                variant="bold/24-32"
                tabletVariant="bold/32-40"
                desktopVariant="bold/40-48"
                color={colorsV2.black100}
                v3
              >
                {title}
              </Typography>
            </HeaderLeft>
            <HeaderRight>
              {!isMobile && <IconUserGroup color={colorsV2.primary100} />}
              {(subtitle || description) && (
                <HeaderRightItem>
                  {subtitle && (
                    <Typography
                      variant="regular/28-34"
                      color={colorsV2.neutral200}
                    >
                      {subtitle}
                    </Typography>
                  )}
                  {description && (
                    <Typography
                      variant="regular/16-24"
                      color={colorsV2.neutral180}
                    >
                      {description}
                    </Typography>
                  )}
                </HeaderRightItem>
              )}
            </HeaderRight>
          </Header>
          <HonorRollList
            data={honorList}
            loading={loading}
            total={total.current}
            loadMore={onLoadMoreClick}
          />
        </Inner>
      </ResponsiveContainer>
    </Wrapper>
  );
};

export default HonorRoll;
